/**
 * <horizontal-scroller>
 * Turns any element carrying `.js-scroller` into a snap-scrolling track and adds prev/next arrows.
 * Arrow markup lives in snippets/horizontal-scroller-controls.liquid.
 * Visuals are handled by horizontal-scroller.css. Custom controls can use `.js-scroller-prev` / `.js-scroller-next`.
 * `--scroller-step: item` makes an arrow click travel exactly one item instead of a page.
 */
const HORIZONTAL_SCROLLER_TEMPLATE_ID = 'horizontal-scroller-controls';
// Fraction of the visible width an arrow click travels in the default `page` mode.
const HORIZONTAL_SCROLLER_PAGE_STEP = 0.8;
// Tolerance for sub-pixel scroll positions on high-DPI screens.
const SCROLL_EPSILON = 1;

class HorizontalScroller extends HTMLElement {
  static #templateWarned = false;

  #scroller = null;
  #state = {};
  #controller = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#scroller = this.querySelector('.js-scroller');
    if (!this.#scroller) return;

    this.#addControls();

    // One AbortController detaches every listener on disconnect.
    const { signal } = (this.#controller = new AbortController());
    const update = () => this.#update();

    this.#scroller.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { signal });
    this.querySelector('.js-scroller-prev')?.addEventListener('click', () => this.#scrollByStep(-1), { signal });
    this.querySelector('.js-scroller-next')?.addEventListener('click', () => this.#scrollByStep(1), { signal });

    // Watch the track *and* its items: lazy images and late webfonts change the
    // content width without resizing the track itself.
    this.#resizeObserver = new ResizeObserver(update);
    this.#observe();

    requestAnimationFrame(update);
  }

  /**
   * Re-measure after the call site has replaced the track's contents. Not a
   * MutationObserver: the sections that fetch their items know exactly when
   * they are done, and every other call site is server-rendered and static.
   */
  refresh() {
    if (!this.#scroller) return;
    this.#observe();
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#controller = null;
    this.#resizeObserver = null;
  }

  #addControls() {
    if (this.querySelector('.js-scroller-prev') || this.querySelector('.js-scroller-next')) return;

    const template = document.getElementById(HORIZONTAL_SCROLLER_TEMPLATE_ID);

    if (!template) {
      if (!HorizontalScroller.#templateWarned) {
        HorizontalScroller.#templateWarned = true;
        console.warn(
          `[horizontal-scroller] #${HORIZONTAL_SCROLLER_TEMPLATE_ID} is missing — the scroller ` +
            'works but has no arrows. Check that layout/theme.liquid renders ' +
            'snippets/horizontal-scroller-controls.liquid.',
        );
      }
      return;
    }

    const controls = template.content.cloneNode(true);
    const prev = controls.querySelector('.js-scroller-prev');
    const next = controls.querySelector('.js-scroller-next');

    if (prev) this.prepend(prev);
    if (next) this.append(next);
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#scroller;

    this.#setState('data-can-scroll-left', scrollLeft > SCROLL_EPSILON);
    this.#setState('data-can-scroll-right', scrollLeft + clientWidth < scrollWidth - SCROLL_EPSILON);
  }

  // Diffed so a scroll tick that changes nothing doesn't invalidate style ~60×/s.
  #setState(name, value) {
    if (this.#state[name] === value) return;
    this.#state[name] = value;
    this.setAttribute(name, value);
  }

  #observe() {
    if (!this.#resizeObserver) return;
    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(this.#scroller);
    Array.from(this.#scroller.children).forEach((child) => this.#resizeObserver.observe(child));
  }

  #stepDistance() {
    // Read as a keyword, never as arithmetic: a custom property holding a
    // calc() comes back from getComputedStyle unresolved unless it is
    // registered with @property, which is above the supported browser floor.
    if (getComputedStyle(this).getPropertyValue('--scroller-step').trim() !== 'item') {
      return this.#scroller.clientWidth * HORIZONTAL_SCROLLER_PAGE_STEP;
    }

    const [first, second] = this.#scroller.children;
    if (!first) return 0;
    // Measuring between two items puts the gap inside the step; falling back to
    // one item's box leaves it out, which only matters when there is nothing to
    // scroll to anyway.
    return second ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width;
  }

  #scrollByStep(direction) {
    // No `behavior` here on purpose: CSS `scroll-behavior` owns it, and it is
    // already gated on prefers-reduced-motion in horizontal-scroller.css.
    this.#scroller.scrollBy({ left: direction * this.#stepDistance() });
  }
}

if (!customElements.get('horizontal-scroller')) {
  customElements.define('horizontal-scroller', HorizontalScroller);
}

/**
 * <slider-pagination>
 * Pagination dots for a row of cards or slides. The track, arrows, and fades belong to <horizontal-scroller>.
 * One dot equals one card/slide. Liquid renders max dots; JS hides unreachable surplus dots.
 */

class SliderPagination extends HTMLElement {
  #track = null;
  #dots = [];
  #offsets = [];
  #lastIndex = 0;
  #activeIndex = -1;
  #controller = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#track = this.querySelector('.js-scroller');
    this.#dots = Array.from(this.querySelectorAll('.js-carousel-dot'));
    if (!this.#track || this.#dots.length === 0) return;

    // One AbortController detaches every listener on disconnect.
    const { signal } = (this.#controller = new AbortController());

    this.#track.addEventListener('scroll', () => this.#syncActive(), { passive: true, signal });
    window.addEventListener('resize', () => this.#measure(), { signal });
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#scrollToIndex(index), { signal });
    });

    // Watch the track *and* each card: lazy images and late webfonts change the
    // content width without resizing the track itself.
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#observe();

    requestAnimationFrame(() => this.#measure());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#controller = null;
    this.#resizeObserver = null;
  }

  /**
   * Re-measure after the section has replaced the track's cards. The sections
   * that fetch their cards call this when the fetch resolves; it forwards to
   * the scroller so the arrows re-evaluate at the same moment.
   */
  refresh() {
    if (!this.#track) return;
    this.querySelector('horizontal-scroller')?.refresh?.();
    this.#observe();
    this.#measure();
  }

  #observe() {
    if (!this.#resizeObserver) return;
    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(this.#track);
    Array.from(this.#track.children).forEach((card) => this.#resizeObserver.observe(card));
  }

  // Card offsets only change on resize or when the cards themselves change, so
  // they are cached here rather than read from the scroll handler, which would
  // force layout on every tick.
  #measure() {
    const cards = Array.from(this.#track.children);
    const origin = cards[0]?.offsetLeft ?? 0;
    this.#offsets = cards.map((card) => card.offsetLeft - origin);

    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    this.#lastIndex = 0;
    this.#offsets.forEach((offset, index) => {
      if (offset <= maxScroll + SCROLL_EPSILON) this.#lastIndex = index;
    });

    this.#dots.forEach((dot, index) => {
      dot.hidden = index >= this.#offsets.length || index > this.#lastIndex;
    });

    // A single reachable position means the row does not scroll, so the dots
    // have nothing to say. CSS hides the row off this attribute.
    this.toggleAttribute('data-dots-idle', this.#lastIndex === 0);

    this.#activeIndex = -1; // the hidden/shown set changed, so force a write
    this.#syncActive();
  }

  #syncActive() {
    const { scrollLeft } = this.#track;

    let index = 0;
    let closest = Infinity;
    this.#offsets.forEach((offset, i) => {
      if (i > this.#lastIndex) return;
      const distance = Math.abs(offset - scrollLeft);
      if (distance < closest) {
        closest = distance;
        index = i;
      }
    });

    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#dots.forEach((dot, i) => {
      dot.classList.toggle('slider-counter__link--active', i === index);
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #scrollToIndex(index) {
    const left = this.#offsets[index];
    if (left === undefined) return;
    // No `behavior` here on purpose: CSS `scroll-behavior` owns it, and it is
    // already gated on prefers-reduced-motion in horizontal-scroller.css.
    this.#track.scrollTo({ left });
  }
}

if (!customElements.get('slider-pagination')) {
  customElements.define('slider-pagination', SliderPagination);
}
